import { formatPropertyAddress } from '../utils/propertyAddress';
import { propertyFetch } from '../services/idxPropertyClient';
import React, { useState, useEffect } from 'react';
import { Helmet } from '@dr.pogodin/react-helmet';
import { useNavigate, useParams, Link, Navigate } from 'react-router-dom';
import { SITE_CONFIG, SEO_CONFIG } from '../config';
import { useTheme } from '../contexts/ThemeContext';
import { warmPropertyBatch } from '../utils/warmPropertyDetail';
import usePropertyDetail from '../hooks/usePropertyDetail';
import PropertyDetailModal from './PropertyDetailModal';
import LocalResourceLinks from './LocalResourceLinks';
import { neighborhoods } from '../data/neighborhoods';
import { guides } from '../data/guides';
import './IDXTownPage.css';

const AGENT_FIRST_NAME = SITE_CONFIG.AGENT.NAME.split(' ')[0];

// Route: /neighborhood/:slug — data-driven from src/data/neighborhoods.js
const NeighborhoodPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [likedProperties, setLikedProperties] = useState(new Set());
  const { propertyDetail, setPropertyDetail, propertyDetailImages, imagesLoading, openPropertyFromSummary } = usePropertyDetail();

  const area = neighborhoods[slug];
  const parentArea = area?.parentSlug ? neighborhoods[area.parentSlug] : null;
  const childCommunities = area
    ? Object.values(neighborhoods).filter((candidate) => candidate.parentSlug === area.slug)
    : [];
  const relatedCommunities = (area?.relatedCommunities || [])
    .map((communitySlug) => neighborhoods[communitySlug])
    .filter(Boolean);
  const relatedGuides = (area?.relatedGuides || [])
    .map((guide) => {
      if (typeof guide === 'string') {
        return guides[guide] ? { slug: guide, title: guides[guide].title } : null;
      }
      return guide;
    })
    .filter(Boolean);

  const openAreaSearch = () => navigate('/', { state: { openGlobeMap: true } });

  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, [isDarkMode]);

  useEffect(() => {
    if (!area) return;
    const fetchProperties = async () => {
      setLoading(true);
      try {
        // The current summary API supports city filters but does not expose a
        // reliable subdivision/community field. Fetch the correct parent city
        // market(s) and label the results honestly as nearby context below.
        const searchCities = area.searchCities || [area.searchCity];
        const results = await Promise.all(searchCities.map(async (city) => {
          const params = new URLSearchParams({
            firstPhoto: 'true',
            hasMedia: 'true',
            listingDateSort: 'newold',
            status: 'Active',
            limit: '36',
            state: SITE_CONFIG.STATE,
            city
          });
          const response = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-summary?${params}`, {});
          if (!response.ok) return [];
          const data = await response.json();
          return Array.isArray(data) ? data : (data.properties || []);
        }));

        const seen = new Set();
        const unique = results.flat().filter((property) => {
          const key = property.listing_key || property.listingKey || property.id;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        setProperties(unique);
      } catch (error) {
        console.error('Error fetching properties:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, [area, slug]);

  useEffect(() => {
    warmPropertyBatch(properties, 12);
  }, [properties]);

  // Unknown slug → send to home rather than render an empty shell.
  if (!area) return <Navigate to="/" replace />;

  const canonical = `${SEO_CONFIG.DOMAIN}/neighborhood/${area.slug}`;
  const socialImage = area.imageUrl?.startsWith('http')
    ? area.imageUrl
    : `${SEO_CONFIG.DOMAIN}${area.imageUrl || ''}`;
  const placeSchema = {
    '@context': 'https://schema.org',
    '@type': 'Place',
    name: `${area.name}, FL`,
    description: area.metaDescription,
    url: canonical,
    ...(socialImage ? { image: socialImage } : {}),
    address: {
      '@type': 'PostalAddress',
      addressLocality: (area.searchCities || [area.searchCity || area.name]).join(' / '),
      addressRegion: 'FL',
      addressCountry: 'US'
    },
    containedInPlace: { '@type': 'AdministrativeArea', name: area.county }
  };
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SEO_CONFIG.DOMAIN}/` },
      ...(parentArea ? [{
        '@type': 'ListItem',
        position: 2,
        name: `${parentArea.name} Real Estate`,
        item: `${SEO_CONFIG.DOMAIN}/neighborhood/${parentArea.slug}`
      }] : []),
      {
        '@type': 'ListItem',
        position: parentArea ? 3 : 2,
        name: area.name,
        item: canonical
      }
    ]
  };
  const faqSchema = area.faqs?.length ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: area.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: { '@type': 'Answer', text: faq.a }
    }))
  } : null;
  const amenities = area.amenities || (area.communityHighlights || []).map((highlight, index) => ({
    h3: ['Community Setting', 'Homes & Views', 'Resident Lifestyle', 'Location & Access'][index] || 'Community Detail',
    p: highlight
  }));

  return (
    <div className="idxtown-page">
      <Helmet>
        <title>{area.metaTitle}</title>
        <meta name="description" content={area.metaDescription} />
        <meta name="keywords" content={area.keywords} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={area.metaTitle} />
        <meta property="og:description" content={area.metaDescription} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        {socialImage && <meta property="og:image" content={socialImage} />}
        <script type="application/ld+json">{JSON.stringify(placeSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        {faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}
      </Helmet>

      <div className="idxtown-container">
        <button className="idxtown-home-btn" onClick={() => navigate('/')}>← Home</button>
        <nav className="neighborhood-breadcrumbs" aria-label="Breadcrumb">
          <Link to="/">Home</Link>
          {parentArea && <><span aria-hidden="true">/</span><Link to={`/neighborhood/${parentArea.slug}`}>{parentArea.name}</Link></>}
          <span aria-hidden="true">/</span><span aria-current="page">{area.name}</span>
        </nav>
        <header className="idxtown-header">
          <span className="idxtown-eyebrow">{area.eyebrow}</span>
          <h1>{area.h1}</h1>
        </header>

        {area.imageUrl && (
          <div className="neighborhood-hero-image">
            <img src={area.imageUrl} alt={`${area.name}, Florida — homes and lifestyle`} loading="eager" fetchPriority="high" />
          </div>
        )}

        {area.directAnswer && (
          <section className="community-quick-answer" aria-labelledby="community-quick-answer-title">
            <span>Quick answer</span>
            <h2 id="community-quick-answer-title">What is it like to live in {area.name}?</h2>
            <p>{area.directAnswer}</p>
          </section>
        )}

        <section className="idxtown-vibe">
          <h2>Living in {area.name}, {area.county}</h2>
          {area.intro.map((para, i) => <p key={i}>{para}</p>)}
        </section>

        <section className="idxtown-lifestyle">
          <h2>Local Lifestyle & Amenities</h2>
          <p>What makes {area.name} a place buyers keep coming back to:</p>
          <div className="amenities-list">
            {amenities.map((a, i) => (
              <div className="amenity" key={i}>
                <h3>{a.h3}</h3>
                <p>{a.p}</p>
              </div>
            ))}
          </div>
        </section>

        {area.buyerNotes?.length > 0 && (
          <section className="idxtown-vibe community-due-diligence">
            <h2>What Buyers Should Verify Before Making an Offer</h2>
            <p>Community information and association terms can change. Use this checklist for property-specific due diligence:</p>
            <ul className="community-checklist">
              {area.buyerNotes.map((note) => <li key={note}>{note}</li>)}
            </ul>
          </section>
        )}

        <section className="market-snapshot">
          <h2>{area.name} Real Estate Market Snapshot</h2>
          <p>Figures below are general, directional context — reach out for a current, address-specific market analysis.</p>
          <table className="market-stats">
            <thead>
              <tr><th>Market Overview</th><th>Details</th></tr>
            </thead>
            <tbody>
              {area.marketStats.map((row, i) => (
                <tr key={i}><td>{row.stat}</td><td>{row.value}</td></tr>
              ))}
            </tbody>
          </table>
        </section>

        <section className="properties-section">
          <div className="properties-section-head">
            <h2>{area.isCommunity ? `Homes for Sale In and Around ${area.name}` : `Current ${area.name} Properties for Sale`}</h2>
            <button type="button" className="area-search-all-btn" onClick={openAreaSearch}>
              {area.isCommunity ? `Search all ${area.searchCities.join(' & ')} properties` : `Search all properties in ${area.name}`} →
            </button>
          </div>
          {area.isCommunity && (
            <p className="community-listing-note">
              MLS community names are not standardized in this feed. These active {area.searchCities.join(' and ')} listings provide nearby market context; contact Peter for a verified search limited to {area.name}'s legal boundaries and subdivision names.
            </p>
          )}
          {loading ? (
            <p>Loading properties...</p>
          ) : properties.length === 0 ? (
            <p>No active listings to display right now — <button type="button" className="area-search-all-btn" onClick={openAreaSearch}>open the live property search →</button></p>
          ) : (
            <div className="properties-grid">
              {properties.slice(0, 12).map((property) => (
                <div
                  key={property.id}
                  className="property-card"
                  role="button"
                  tabIndex={0}
                  onClick={() => openPropertyFromSummary(property)}
                  onKeyDown={(e) => { if (e.key === 'Enter') openPropertyFromSummary(property); }}
                  style={{
                    backgroundImage: property.first_photo_url ? `url(${property.first_photo_url})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  <div className="property-price">${property.current_price?.toLocaleString()}</div>
                  <div className="property-address">
                    {formatPropertyAddress(property)}
                  </div>
                  <div className="property-type">{property.property_sub_type}</div>
                </div>
              ))}
            </div>
          )}
        </section>

        {area.faqs?.length > 0 && (
          <section className="idxtown-lifestyle community-faqs">
            <h2>Frequently Asked Questions About {area.name}</h2>
            <div className="amenities-list">
              {area.faqs.map((faq) => (
                <div className="amenity" key={faq.q}>
                  <h3>{faq.q}</h3>
                  <p>{faq.a}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {childCommunities.length > 0 && (
          <section className="idxtown-lifestyle">
            <h2>Explore Communities in {area.name}</h2>
            <p>Use these more detailed community guides to compare housing, amenities, association structures and buyer considerations.</p>
            <ul className="neighborhood-resources community-link-grid">
              {childCommunities.map((community) => (
                <li key={community.slug}><Link to={`/neighborhood/${community.slug}`}>{community.name} Homes for Sale &amp; Community Guide</Link></li>
              ))}
            </ul>
          </section>
        )}

        {relatedCommunities.length > 0 && (
          <section className="idxtown-lifestyle">
            <h2>Compare Nearby Communities</h2>
            <ul className="neighborhood-resources community-link-grid">
              {parentArea && <li><Link to={`/neighborhood/${parentArea.slug}`}>{parentArea.name} Real Estate &amp; City Guide</Link></li>}
              {relatedCommunities.map((community) => (
                <li key={community.slug}><Link to={`/neighborhood/${community.slug}`}>{community.name} Homes for Sale</Link></li>
              ))}
            </ul>
          </section>
        )}

        {relatedGuides.length > 0 && (
          <section className="idxtown-lifestyle">
            <h2>Helpful Resources</h2>
            <p>Local guides to help you buy or sell in {area.name} and across South Florida:</p>
            <ul className="neighborhood-resources">
              {relatedGuides.map((g) => (
                <li key={g.slug}><Link to={`/guide/${g.slug}`}>{g.title}</Link></li>
              ))}
              <li><Link to="/agent">Meet {SITE_CONFIG.AGENT.NAME}</Link></li>
            </ul>
          </section>
        )}

        <section className="cta-section">
          <h2>Ready to Explore {area.name} Homes?</h2>
          <p>Contact {SITE_CONFIG.AGENT.NAME}, your trusted South Florida real estate expert, to schedule a tour of available {area.name} properties.</p>
          <div className="cta-buttons">
            <a href={`mailto:${SITE_CONFIG.AGENT.EMAIL}?subject=${encodeURIComponent(area.name + ' Home Inquiry')}`} className="cta-button primary">
              Email {AGENT_FIRST_NAME}
            </a>
            <a href={`tel:${SITE_CONFIG.AGENT.PHONE}`} className="cta-button secondary">
              Call {SITE_CONFIG.AGENT.PHONE.replace('+1-', '')}
            </a>
          </div>
        </section>
      </div>

      <LocalResourceLinks variant="area" />

      <div style={{background: 'rgba(0,0,0,0.85)', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap'}}>
        {SITE_CONFIG.MLS.LOGO_URL && <img src={SITE_CONFIG.MLS.LOGO_URL} alt={SITE_CONFIG.MLS.SHORT_NAME} style={{height: '20px', objectFit: 'contain'}} />}
        <span style={{fontSize: '10px', color: '#ccc', flex: 1}}>{SITE_CONFIG.MLS.DISCLAIMER} | Agent License: {SITE_CONFIG.AGENT.LICENSE}</span>
      </div>

      {propertyDetail && (
        <PropertyDetailModal
          propertyDetail={propertyDetail}
          propertyDetailImages={propertyDetailImages}
          imagesLoading={imagesLoading}
          onClose={() => setPropertyDetail(null)}
          user={null}
          isAdmin={false}
          likedProperties={likedProperties}
          setLikedProperties={setLikedProperties}
        />
      )}
    </div>
  );
};

export default NeighborhoodPage;
